import { Alert } from 'antd';
import { useTranslation } from 'react-i18next';

import MedicationReconciliationItem from './item';
import MedicationReconciliationHeader from './header';
import s from './styles.module.scss';

import type { IMedication } from 'infrastructure/interfaces';
import type { FC } from 'react';

interface Props {
  items: IMedication[];

  lastSync?: string;
  source?: string;

  onAccept: (item: IMedication) => void;
  onIgnore: (guid: string) => void;

  onAcceptAll: () => void;
  onIgnoreAll: () => void;

  disableActions: boolean;
  remediatingGuid?: string;
  remediatingActionType?: 'accept' | 'ignore' | null;
  isRemediating?: boolean;
  bulkActionType?: 'acceptAll' | 'ignoreAll' | null;
  modifyItemGuid?: string;

  importSkippedWarnings?: string[];
  onCloseWarnings?: () => void;
}

const MedicationReconciliation: FC<Props> = ({
  items,
  lastSync,
  source,
  onAccept,
  onIgnore,
  onAcceptAll,
  onIgnoreAll,
  disableActions,
  importSkippedWarnings = [],
  onCloseWarnings,
  isRemediating,
  bulkActionType,
  remediatingGuid,
  remediatingActionType,
  modifyItemGuid,
}) => {
  const { t } = useTranslation();
  const added = items.filter((i) => i.type === 'added').length;
  const updated = items.filter((i) => i.type === 'updated').length;
  const removed = items.filter((i) => i.type === 'removed').length;

  const formatWarningMessage = () => {
    if (importSkippedWarnings.length === 0) return '';

    const details = importSkippedWarnings.join(', ');
    return t('labels.someRowsWereSkipped', { details });
  };

  const isBulkRemediating = Boolean(isRemediating && !remediatingGuid);
  const loadingAcceptAll = isBulkRemediating && bulkActionType === 'acceptAll';
  const loadingIgnoreAll = isBulkRemediating && bulkActionType === 'ignoreAll';
  const allUnrecognized =
    items.length > 0 && items.every((i) => i.type === 'unrecognized');
  const disableAcceptAll =
    disableActions || loadingIgnoreAll || allUnrecognized;
  const disableIgnoreAll = disableActions || loadingAcceptAll;

  return (
    <div className={s.container}>
      <MedicationReconciliationHeader
        total={items.length}
        added={added}
        updated={updated}
        removed={removed}
        lastSync={lastSync}
        source={source}
        loadingAcceptAll={loadingAcceptAll}
        loadingIgnoreAll={loadingIgnoreAll}
        disableAcceptAll={disableAcceptAll}
        disableIgnoreAll={disableIgnoreAll}
        onAcceptAll={onAcceptAll}
        onIgnoreAll={onIgnoreAll}
      />
      {importSkippedWarnings.length > 0 && (
        <Alert
          message={formatWarningMessage()}
          type="error"
          showIcon
          closable
          onClose={onCloseWarnings}
          className={s['skipped-row-error']}
        />
      )}
      {items.map((item) => (
        <MedicationReconciliationItem
          key={item.guid}
          item={item}
          status={item.type}
          onAccept={onAccept}
          onIgnore={onIgnore}
          loading={remediatingGuid === item.guid}
          disableActions={disableActions}
          remediatingActionType={remediatingActionType}
          modifyItemGuid={modifyItemGuid}
        />
      ))}
    </div>
  );
};

export default MedicationReconciliation;
